import React from 'react';
import { Truck, Wrench, Award, Shield } from 'lucide-react';

function ImageOptionsDemo() {
  // Using placeholder images from Unsplash
  const heroImage = "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&h=1080&fit=crop";
  const serviceImages = [
    "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=600&fit=crop"
  ];
  const galleryImages = [
    "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=600&h=400&fit=crop",
    "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&h=400&fit=crop"
  ];

  return (
    <div className="bg-gray-900 min-h-screen">
      <div className="bg-black text-white py-4 px-6 sticky top-0 z-50">
        <h1 className="text-2xl font-bold text-green-400">Photo Display Options Demo</h1>
        <p className="text-gray-400">Scroll to see different layout styles</p>
      </div>

      {/* OPTION 1: Hero with Full Background Image */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 1: Hero Background Image</h2>
          <p className="text-gray-300">Full-width background with dark overlay</p>
        </div>
        <div className="relative h-[600px] flex items-center justify-center">
          <img 
            src={heroImage}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-6xl font-black mb-4">De Rose Transport</h1>
            <p className="text-2xl font-bold text-green-400 mb-4">Family Owned & Operated</p>
            <p className="text-xl text-gray-200 mb-8">Specializing in Oilfield Hauling & SAGD Support</p>
            <button className="bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg">
              Request a Quote
            </button>
          </div>
        </div>
      </section>

      {/* OPTION 2: Service Cards with Images */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 2: Service Cards with Images</h2>
          <p className="text-gray-300">Image on top, text below</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceImages.map((img, idx) => (
              <div key={idx} className="bg-gray-800 border border-green-500/30 rounded-lg overflow-hidden hover:border-green-500 transition-all">
                <img src={img} alt={`Service ${idx + 1}`} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-green-400 mb-2">Oilfield Transport</h3>
                  <p className="text-gray-300">Professional heavy equipment transportation across Western Canada.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPTION 3: Image Grid/Gallery */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 3: Equipment Gallery Grid</h2>
          <p className="text-gray-300">Photo gallery layout</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-8 text-center">Our Fleet</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryImages.map((img, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-lg border-2 border-green-500/30 hover:border-green-500 transition-all cursor-pointer group">
                  <img src={img} alt={`Equipment ${idx + 1}`} className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                    <p className="text-white font-bold p-4">Heavy Hauler Truck</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPTION 4: Alternating Image/Text */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 4: Alternating Image/Text Layout</h2>
          <p className="text-gray-300">Image left, text right (then alternates)</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-7xl mx-auto space-y-16">
            {/* Row 1: Image Left */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <img src={serviceImages[0]} alt="Service" className="w-full h-80 object-cover rounded-lg border-2 border-green-500/30" />
              <div>
                <h3 className="text-3xl font-bold text-green-400 mb-4">Professional Service</h3>
                <p className="text-gray-300 text-lg mb-4">
                  Our experienced team delivers reliable heavy equipment transportation services across Alberta and Western Canada.
                </p>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> 24/7 Availability</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> SAGD Ready</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Fully Certified</li>
                </ul>
              </div>
            </div>
            {/* Row 2: Text Left, Image Right */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-green-400 mb-4">Modern Fleet</h3>
                <p className="text-gray-300 text-lg mb-4">
                  State-of-the-art equipment maintained to the highest standards for safe and efficient operations.
                </p>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Heavy Haulers</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Cranes & Pickers</li>
                  <li className="flex items-center"><span className="text-green-400 mr-2">✓</span> Off-Highway Vehicles</li>
                </ul>
              </div>
              <img src={serviceImages[1]} alt="Fleet" className="w-full h-80 object-cover rounded-lg border-2 border-green-500/30" />
            </div>
          </div>
        </div>
      </section>

      {/* OPTION 5: Cards with Icon Overlay */}
      <section className="mb-16 border-b-4 border-green-500">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 5: Image Cards with Icon Overlay</h2>
          <p className="text-gray-300">Image background with icon and text overlay</p>
        </div>
        <div className="px-6 py-12 bg-gray-900">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <Truck size={48} />, title: "Transport", img: serviceImages[0] },
              { icon: <Wrench size={48} />, title: "Equipment", img: serviceImages[1] },
              { icon: <Award size={48} />, title: "Certified", img: serviceImages[2] },
              { icon: <Shield size={48} />, title: "Safety", img: galleryImages[0] }
            ].map((item, idx) => (
              <div key={idx} className="relative h-80 rounded-lg overflow-hidden border-2 border-green-500/30 hover:border-green-500 transition-all cursor-pointer group">
                <img src={item.img} alt={item.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <div className="text-green-400 mb-4">{item.icon}</div>
                  <h3 className="text-2xl font-bold">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPTION 6: Large Feature Image with Stats */}
      <section className="mb-16">
        <div className="bg-gray-800 px-6 py-4">
          <h2 className="text-3xl font-bold text-green-400">Option 6: Feature Image with Stats Overlay</h2>
          <p className="text-gray-300">Background image with statistics cards</p>
        </div>
        <div className="relative h-[500px]">
          <img src={heroImage} alt="Background" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70"></div>
          <div className="relative z-10 h-full flex items-center justify-center px-6">
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { stat: "24/7", label: "Service" },
                { stat: "100%", label: "SAGD Ready" },
                { stat: "45T", label: "Max Capacity" },
                { stat: "WC", label: "Western Canada" }
              ].map((item, idx) => (
                <div key={idx} className="bg-black/60 backdrop-blur-sm border-2 border-green-500/50 rounded-lg p-8 text-center">
                  <div className="text-5xl font-black text-green-400 mb-2">{item.stat}</div>
                  <div className="text-gray-300">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ImageOptionsDemo;
